import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { HelpersBffService, subjectPseudoId } from '@app/core/services/bff/helpers.service';
import { TeachingSubject, UserProfileBffService, UserProfileInfo } from '@app/core/services/bff/user-profile.service';
import { NotFoundError } from '@app/lingo2-errors';
import { AccountService, ContextService, MetaService, PlatformService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { loadCurrentUserBySlug } from '@store/actions/users.actions';
import { IImageFile, Language, Subject, User, UserStatusEnum } from 'lingo2-models';
import { trimStart } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, Observable, of, filter, map, mergeAll, takeUntil, tap, switchMap } from 'rxjs';
import { RESPONSE } from '../../../../express.tokens';
import { DictionaryService } from './../../../core/services/bff/dictionary.service';

@Component({
  selector: 'app-user-profile-page',
  templateUrl: './user-profile-page.component.html',
  styleUrls: ['./user-profile-page.component.scss'],
})
export class UserProfilePageComponent extends ChangableComponent implements OnInit, OnDestroy {
  @Input() public set userName(value: string) {
    this._username = value;
    this.initWatchers();
  }
  public get userName(): string {
    return this._username;
  }
  @Input() isModal: boolean;
  @Input() selectedSubjectId: string | number;

  @Output() public isCloseAllModals = new EventEmitter<boolean>();

  public loaded: boolean;
  public loading: boolean;
  public user: User;
  public me: User;
  public _username: string;
  public profile: UserProfileInfo;
  public subjects: Subject[] = [];
  public languages: Language[] = [];
  public accountBlocked = false;
  protected isMe: boolean;
  protected isTeacher: boolean;
  protected paramSubpage:
    | 'teaching'
    | 'about'
    | 'publications'
    | 'collections'
    | 'feedback'
    | 'classes'
    | 'lessons'
    | 'schools';
  protected paramSubject: string;
  protected subject: TeachingSubject;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    private contextService: ContextService,
    private accountService: AccountService,
    private route: ActivatedRoute,
    @Optional() @Inject(RESPONSE) private response: any,
    private meta: MetaService,
    private router: Router,
    private store: Store,
    public deviceService: DeviceDetectorService,
    protected userProfileBff: UserProfileBffService,
    protected dictionaryService: DictionaryService,
    protected helperService: HelpersBffService,
    private translate: TranslateService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  public ngOnInit(): void {
    this.initWatchers();
  }

  initWatchers(): void {
    this.contextService.me$
      .pipe(
        tap((me) => (this.me = me)),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    of(this.watchReferences$, this.watchRouteParamMap$)
      .pipe(mergeAll())
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => this.init(),
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.watchMeta$.pipe(takeUntil(this.destroyed$)).subscribe({
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.meta.reset();
  }

  protected get watchReferences$(): Observable<any> {
    return combineLatest([this.dictionaryService.subjects, this.dictionaryService.languages]).pipe(
      tap(([subjects, languages]) => {
        this.subjects = this.helperService.convertBffSubjectsToLegacy(subjects);
        this.languages = this.helperService.convertBffLanguagesToLegacy(languages);
      }),
    );
  }

  protected get watchRouteParamMap$(): Observable<any> {
    return this.route.paramMap.pipe(tap((params) => this.loadUser(this.userName ? this.userName : params.get('user'))));
  }

  protected get watchMeta$(): Observable<any> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      tap((event) => {
        this.parseUrl(event.url);
        this.checkParamSubject();
        this.setupMeta();
      }),
    );
  }

  protected loadUser(slug) {
    if (!slug || this.loading) {
      return;
    }
    this.loading = true;
    this.accountService
      .getUserBySlug(slug)
      .pipe(
        map((user) => {
          this.user = user;
          this.store.dispatch(loadCurrentUserBySlug({ slug })); // без этого перестал работать сайдбар
          this.contextService.updateUser(user);
          if (this.assertBlockedAccount(user)) {
            this.loading = false;
            this.detectChanges();
            return;
          }
          return user;
        }),
        switchMap((user) => this.userProfileBff.getUserProfileInfo({ id: user.id })),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (profile) => {
          this.profile = profile;
          this.contextService.updateBffProfile(profile);
          this.loading = false;
          this.init();
          if (this.isModal) {
            this.detectChanges();
          }
        },
        error: (err: any) => {
          this.handleError(err);
        },
      });
  }

  public handleError(err) {
    this.loading = false;
    if (err instanceof NotFoundError) {
      this.meta.setTitle(['account.not-found.title']);
      // TODO remove meta
      if (this.response) {
        this.response.statusCode = 404;
        this.response.statusMessage = 'Page Not Found';
      }
    }
    this.detectChanges();
  }

  protected init() {
    this.loaded = !!this.user && !!this.profile && this.subjects.length > 0;
    this.isMe = this.me && this.user && this.me.id === this.user.id;
    this.isTeacher = this.isMe ? AccountService.isAsIfTeacher(this.user) : AccountService.isTeacher(this.user);

    this.parseUrl(this.router.url);
    this.checkParamSubject();
    this.setupMeta();
  }

  protected parseUrl(url: string) {
    // logger.log('UserProfilePagesComponent:parseUrl', url);
    const urlParts = trimStart(url, '/').split('/');

    if (urlParts.length > 2) {
      // если подраздел уже выбран

      switch (urlParts[2]) {
        case 'teaching': // /u/{slug}/teaching
          this.paramSubpage = 'teaching';
          this.paramSubject = urlParts[3] || null;
          this.checkParamSubject();
          break;

        case 'classes': // /u/{slug}/classes
          this.paramSubpage = 'classes';
          this.paramSubject = urlParts[3] || null;
          this.checkParamSubject();
          break;

        case 'lessons': // /u/{slug}/lessons
          this.paramSubpage = 'lessons';
          this.paramSubject = urlParts[3] || null;
          this.checkParamSubject();
          break;

        case 'collections': // /u/{slug}/collections
          this.paramSubpage = 'collections';
          break;

        case 'schools': // /u/{slug}/schools
          this.paramSubpage = 'schools';
          break;

        case 'publications': // /u/{slug}/publications
          this.paramSubpage = 'publications';
          break;

        case 'feedback': // /u/{slug}/feedback
          this.paramSubpage = 'feedback';
          break;

        case 'about': // /u/{slug}/about
          this.paramSubpage = 'about';
          this.paramSubject = urlParts[3] || null;
          this.checkParamSubject();
          break;
      }
    } else if (this.loaded) {
      // если подраздел ещё не выбран: /u/{slug}
      // переадресовать на первый доступный подраздел
      if (this.isTeacher) {
        this.paramSubpage = 'teaching';
        this.checkParamSubject();
      } else {
        this.navigateTo(this.accountRoute('about'));
      }
    }
  }

  /**
   * Проверяет параметр subject_id из URL
   */
  protected checkParamSubject() {
    this.subject = null;
    const pagesWithSubject = ['teaching', 'classes', 'lessons', 'about'];
    if (pagesWithSubject.includes(this.paramSubpage)) {
      if (this.profile) {
        // поискать в профиле дисциплину по subject_id из параметров URL
        const subject = this.paramSubject
          ? this.profile?.teachingSubjects?.find((s) => subjectPseudoId(s) === this.paramSubject)
            ? this.profile.teachingSubjects.find((s) => s.subjectId.toString() === this.paramSubject)
            : null
          : null;
        if (subject) {
          this.subject = subject;
          if (this.isBrowser) {
            this.navigateTo(this.accountRoute(this.paramSubpage, subject.subjectId.toString()));
          }
        } else {
          const firstSubject = this.profile.teachingSubjects?.length ? this.profile.teachingSubjects[0] : null;
          if (firstSubject) {
            const subjectId = firstSubject.subjectId.toString();
            // переключиться на первую из дисципилин
            this.subject = firstSubject;
            if (!this.isModal) {
              this.navigateTo(this.accountRoute(this.paramSubpage, subjectId));
            }
          } else {
            this.navigateTo(this.accountRoute(this.paramSubpage));
          }
        }
      }
    } else {
      this.paramSubject = null;
    }
  }

  protected navigateTo(route: string[]) {
    this.onBrowserOnly(() => {
      this.router.navigate(route, { replaceUrl: true }).then(() => true);
    });
    this.onServerOnly(() => {
      this.response.redirect(301, route.join('/'));
    });
  }

  public accountRoute(page?: string, pageParam?: string): string[] {
    const extra = [];
    if (page) {
      extra.push(page);
      if (pageParam) {
        extra.push(pageParam);
      }
    }
    return AccountService.accountRoute(this.user, extra);
  }

  protected get metaImage(): IImageFile {
    if (this.user && this.user.userpic) {
      return this.user.userpic.lgX2 || this.user.userpic.lg || this.user.userpic.mdX2 || this.user.userpic.md;
    }
    return null;
  }

  protected get metaCanonicalUrl(): string {
    switch (this.paramSubpage) {
      case 'teaching':
        return this.accountRoute(this.paramSubpage, this.paramSubject).join('/');

      case 'about':
      case 'publications':
        return this.accountRoute(this.paramSubpage).join('/');

      default:
        return this.accountRoute().join('/');
    }
  }

  protected getTeachingSubjects(): string[] {
    return (this.profile.teachingSubjects || [])
      .filter((ts) => ts.subjectId > 0)
      .map((subject) => {
        const _subject = this.subjects.find((s) => s.id === +subject.subjectId);
        return (_subject ? _subject.title : null) || '---';
      });
  }

  private findSubjectById(id: string): Subject {
    return this.subjects.find((_s) => +_s.id === +id);
  }

  protected assertBlockedAccount(user: User): boolean {
    const isBlocked = [UserStatusEnum.banned, UserStatusEnum.deleted, UserStatusEnum.guest].includes(user.status);
    const isMe = this.me && this.user && this.me.id === this.user.id;

    if (isBlocked && !isMe) {
      this.meta.noIndex();

      this.onServerOnly(() => {
        if (this.response) {
          this.response.statusCode = 404;
          this.response.status = 'Account not found';
        }
      });

      this.accountBlocked = true;
      return true;
    }
    return false;
  }

  public closeAllModals() {
    this.isCloseAllModals.emit(true);
  }

  protected setupMeta() {
    if (!this.loaded) {
      return;
    }

    const name = AccountService.getUserFullName(this.user);
    this.meta.setTypeProfile();

    this.meta.setUrl(this.metaCanonicalUrl);

    const image = this.metaImage;
    if (image) {
      this.meta.setImage(this.user.userpic_id, '', 1 * (image.width as any), 1 * (image.height as any));
    }

    // TODO profile.about
    // TODO Изучаю .... или преподаю ...
    //
    // this.meta.setKeywords(this.meeting.keywords);

    let title = name;
    let description;
    if (AccountService.isTeacher(this.user)) {
      const [u, user, subpage, subjectId, ...others] = trimStart(this.router.url, '/').split('/');

      switch (subpage) {
        case 'teaching':
          title = this.translate.instant('user-profile.meta-teacher-title', {
            subjects: this.findSubjectById(subjectId)?.title || '',
            name,
          });
          description = this.profile?.teachingSubjects?.find((_ts) => +_ts.subjectId === +subjectId)?.description;
          break;
        case 'about':
          title = this.translate.instant('user-profile.meta-teacher-title', {
            subjects: this.getTeachingSubjects().join(', '),
            name,
          });
          description = this.profile?.about;
          break;
        case 'publications':
          title = this.translate.instant('user-profile.meta-teacher-title2', {
            subjects: this.getTeachingSubjects().join(', '),
            name,
          });
          description = title;
          break;
        case 'collections':
          title = this.translate.instant('user-profile.meta-teacher-title2', {
            subjects: this.getTeachingSubjects().join(', '),
            name,
          });
          description = title;
          break;
      }
      this.meta.setTitle([title]);
      this.meta.setDescription(description);
    }
  }
}
